import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { SkeletonList } from '../components/ui/Skeleton';
import {
  AUFTRAG_STATUS_BADGE,
  AUFTRAG_STATUS_LABEL,
  AUFTRAG_TYP_LABEL,
  auftraegeApi,
  type Auftrag,
  type AuftragStatus,
  type AuftragTyp,
} from '../lib/auftraege-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/auftraege/')({
  beforeLoad: () => requireAccess('/api/v1/auftraege'),
  component: AuftraegeIndexPage,
});

const STATUS_FILTER: Array<{ value: '' | AuftragStatus; label: string }> = [
  { value: '', label: 'Alle Status' },
  { value: 'entwurf', label: 'Entwurf' },
  { value: 'bestaetigt', label: 'Bestätigt' },
  { value: 'geplant', label: 'Geplant' },
  { value: 'in_arbeit', label: 'In Arbeit' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
];

const TYP_FILTER: Array<{ value: '' | AuftragTyp; label: string }> = [
  { value: '', label: 'Alle Typen' },
  { value: 'reparatur', label: 'Reparatur' },
  { value: 'wartung', label: 'Wartung' },
  { value: 'notdienst', label: 'Notdienst' },
  { value: 'kontrollgang', label: 'Kontrollgang' },
  { value: 'serienleistung', label: 'Serienleistung' },
  { value: 'reklamation', label: 'Reklamation' },
];

function AuftraegeIndexPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<'' | AuftragStatus>('');
  const [typFilter, setTypFilter] = useState<'' | AuftragTyp>('');
  const auftraegeQ = useQuery({
    queryKey: ['auftraege', 'list'],
    queryFn: () => auftraegeApi.list(),
  });

  const items = auftraegeQ.data?.items ?? [];
  const filtered = items.filter(
    (a) =>
      (statusFilter === '' || a.status === statusFilter) &&
      (typFilter === '' || a.typ === typFilter),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Aufträge"
        description="Operative Aufträge — Reparaturen, Wartungen, Reklamationen."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/auftraege/offene-entscheidungen"
              className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
            >
              Offene Entscheidungen
            </Link>
            <Link
              to="/auftraege/neu"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Neuer Auftrag
            </Link>
          </div>
        }
      />

      {auftraegeQ.error ? <ErrorBanner error={auftraegeQ.error as Error} /> : null}

      <div className="flex flex-wrap gap-3">
        <label className="min-w-[160px] flex-1">
          <span className="mb-1 block text-sm font-medium text-slate-700">Status</span>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | AuftragStatus)}
          >
            {STATUS_FILTER.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>
        <label className="min-w-[160px] flex-1">
          <span className="mb-1 block text-sm font-medium text-slate-700">Typ</span>
          <Select
            value={typFilter}
            onChange={(e) => setTypFilter(e.target.value as '' | AuftragTyp)}
          >
            {TYP_FILTER.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>
      </div>

      {auftraegeQ.isLoading ? (
        <SkeletonList />
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          Keine Aufträge in dieser Auswahl.
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((a) => (
            <AuftragCard key={a.id} auftrag={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AuftragCard({ auftrag }: { auftrag: Auftrag }): JSX.Element {
  return (
    <li>
      <Link
        to="/auftraege/$id"
        params={{ id: auftrag.id }}
        className="block rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-medium text-slate-900">{auftrag.titel}</div>
            <div className="mt-1 text-xs text-slate-500">
              {AUFTRAG_TYP_LABEL[auftrag.typ]} · erstellt{' '}
              {formatGermanDateFromTimestamp(auftrag.createdAt)}
            </div>
          </div>
          <span
            className={`shrink-0 rounded-md border px-2 py-1 text-xs font-medium ${AUFTRAG_STATUS_BADGE[auftrag.status]}`}
          >
            {AUFTRAG_STATUS_LABEL[auftrag.status]}
          </span>
        </div>
      </Link>
    </li>
  );
}
